import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const BasicDetailsStep = ({ formData, updateFormData, validationErrors }) => {
  const [showCustomOccasion, setShowCustomOccasion] = useState(false);
  const [showRecipientForm, setShowRecipientForm] = useState(false);
  const [recipients, setRecipients] = useState([]);

  // Popular occasion types
  const occasionTypes = [
    { id: 'birthday', label: 'Birthday', icon: 'Cake' },
    { id: 'anniversary', label: 'Anniversary', icon: 'Heart' },
    { id: 'wedding', label: 'Wedding', icon: 'Gift' },
    { id: 'graduation', label: 'Graduation', icon: 'GraduationCap' },
    { id: 'baby_shower', label: 'Baby Shower', icon: 'Baby' },
    { id: 'housewarming', label: 'Housewarming', icon: 'Home' },
    { id: 'holiday', label: 'Holiday', icon: 'Star' },
    { id: 'retirement', label: 'Retirement', icon: 'Trophy' },
    { id: 'promotion', label: 'Promotion', icon: 'Award' },
    { id: 'thank_you', label: 'Thank You', icon: 'ThumbsUp' },
    { id: 'get_well', label: 'Get Well', icon: 'Heart' },
    { id: 'custom', label: 'Custom', icon: 'Plus' }
  ];

  // Mock recipient data
  const mockRecipients = [
    { id: 1, name: 'Sarah Johnson', relationship: 'Mother', avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100&h=100&fit=crop&crop=face' },
    { id: 2, name: 'Michael Chen', relationship: 'Friend', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face' },
    { id: 3, name: 'Emma Wilson', relationship: 'Colleague', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face' },
    { id: 4, name: 'David Rodriguez', relationship: 'Brother', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face' }
  ];

  useEffect(() => {
    setRecipients(mockRecipients);
  }, []);

  const handleOccasionTypeSelect = (type) => {
    if (type === 'custom') {
      setShowCustomOccasion(true);
      updateFormData('occasionType', '');
    } else {
      setShowCustomOccasion(false);
      updateFormData('occasionType', type);
      updateFormData('customOccasionType', '');
    }
  };

  const handleRecipientSelect = (recipient) => {
    updateFormData('recipient', recipient.name);
    updateFormData('recipientId', recipient.id);
    setShowRecipientForm(false);
  };

  const handleCustomRecipient = () => {
    setShowRecipientForm(true);
  };

  const handleNewRecipientSubmit = (name) => {
    updateFormData('recipient', name);
    updateFormData('recipientId', null);
    setShowRecipientForm(false);
  };

  // Get minimum date (today)
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="space-y-8">
      {/* Step Title */}
      <div className="text-center">
        <h2 className="font-heading font-semibold text-2xl text-accent-800 mb-2">
          Basic Details
        </h2>
        <p className="text-text-secondary">
          Let's start with the essentials - what's the occasion and who's it for?
        </p>
      </div>

      {/* Occasion Type Selection */}
      <div>
        <label className="block text-sm font-medium text-accent-800 mb-3">
          What's the occasion? *
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {occasionTypes.map((type) => (
            <button
              key={type.id}
              onClick={() => handleOccasionTypeSelect(type.id)}
              className={`p-4 rounded-xl border-2 transition-all duration-200 hover:shadow-md ${
                formData.occasionType === type.id
                  ? 'border-primary bg-primary-50 text-primary' :'border-accent-200 hover:border-accent-300 text-accent-600'
              }`}
            >
              <div className="flex flex-col items-center space-y-2">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  formData.occasionType === type.id
                    ? 'bg-primary text-white' :'bg-accent-100 text-accent-600'
                }`}>
                  <Icon name={type.icon} size={18} />
                </div>
                <span className="text-sm font-medium">{type.label}</span>
              </div>
            </button>
          ))}
        </div>
        
        {showCustomOccasion && (
          <div className="mt-4">
            <Input
              label="Custom Occasion Type"
              value={formData.customOccasionType}
              onChange={(e) => updateFormData('customOccasionType', e.target.value)}
              placeholder="e.g., Housewarming, Job Promotion, etc."
              error={validationErrors.customOccasionType}
            />
          </div>
        )}
        
        {validationErrors.occasionType && (
          <p className="text-red-500 text-sm mt-2">{validationErrors.occasionType}</p>
        )}
      </div>

      {/* Date and Time */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-accent-800 mb-3">
            Date *
          </label>
          <div className="relative">
            <input
              type="date"
              value={formData.date}
              onChange={(e) => updateFormData('date', e.target.value)}
              min={today}
              className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-200 ${
                validationErrors.date
                  ? 'border-red-300 focus:border-red-400' :'border-accent-200 focus:border-primary'
              }`}
            />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <Icon name="Calendar" size={18} className="text-accent-400" />
            </div>
          </div>
          {validationErrors.date && (
            <p className="text-red-500 text-sm mt-2">{validationErrors.date}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-accent-800 mb-3">
            Time (optional)
          </label>
          <div className="relative">
            <input
              type="time"
              value={formData.time}
              onChange={(e) => updateFormData('time', e.target.value)}
              className="w-full px-4 py-3 rounded-xl border-2 border-accent-200 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary-200 transition-all duration-200"
            />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <Icon name="Clock" size={18} className="text-accent-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Recipient Selection */}
      <div>
        <label className="block text-sm font-medium text-accent-800 mb-3">
          Who is this for? *
        </label>
        
        {!showRecipientForm ? (
          <div className="space-y-4">
            {/* Existing Recipients */}
            {recipients.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {recipients.map((recipient) => (
                  <button
                    key={recipient.id}
                    onClick={() => handleRecipientSelect(recipient)}
                    className={`p-4 rounded-xl border-2 transition-all duration-200 hover:shadow-md text-left ${
                      formData.recipientId === recipient.id
                        ? 'border-primary bg-primary-50' :'border-accent-200 hover:border-accent-300'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <img
                        src={recipient.avatar}
                        alt={recipient.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <p className="font-medium text-accent-800">{recipient.name}</p>
                        <p className="text-sm text-text-secondary">{recipient.relationship}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Add New Recipient */}
            <Button
              variant="outline"
              iconName="Plus"
              iconPosition="left"
              onClick={handleCustomRecipient}
              className="w-full border-dashed border-accent-300 text-accent-600 hover:border-accent-400"
            >
              Add New Recipient
            </Button>
          </div>
        ) : (
          /* New Recipient Form */
          <div className="space-y-4">
            <Input
              label="Recipient Name"
              value={formData.recipient}
              onChange={(e) => updateFormData('recipient', e.target.value)}
              placeholder="Enter recipient's name"
              error={validationErrors.recipient}
            />
            <div className="flex space-x-3">
              <Button
                variant="primary"
                onClick={() => handleNewRecipientSubmit(formData.recipient)}
                disabled={!formData.recipient.trim()}
              >
                Add Recipient
              </Button>
              <Button
                variant="ghost"
                onClick={() => setShowRecipientForm(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
        
        {validationErrors.recipient && (
          <p className="text-red-500 text-sm mt-2">{validationErrors.recipient}</p>
        )}
      </div>
    </div>
  );
};

export default BasicDetailsStep;